import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../ui/Layout';
import Card from '../ui/Card';
import Button from '../ui/Button';
import InputField from '../ui/InputField';
import { Invoice } from '../../domain/entities/Invoice';
import { useValidacionDiaHabil } from '../hooks/useValidacionDiaHabil';
import ModalDiaNoHabil from '../ui/ModalDiaNoHabil';
import { formatDate } from '../utils/dateUtils';
import './InvoiceDetailPage.css';

const InvoiceDetailPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Declaramos los hooks primero, antes de cualquier lógica condicional
  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  const [isPartialPayment, setIsPartialPayment] = useState<boolean>(false);
  const [showPartialPaymentMessage, setShowPartialPaymentMessage] = useState<boolean>(false);
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const { validando, mensaje, mostrarModal, validarDiaHabil, aceptarAviso } = useValidacionDiaHabil();

  // Usar useEffect para verificar y configurar la factura desde location.state
  useEffect(() => {
    if (location.state && 'invoice' in location.state) {
      const invoiceData = (location.state as { invoice: Invoice }).invoice;
      if (invoiceData && invoiceData.totalAmount) {
        setInvoice(invoiceData);
        setPaymentAmount(invoiceData.totalAmount);
      }
    }
  }, [location.state]);

  // Si no hay factura disponible, mostrar un mensaje de error
  if (!invoice) {
    return (
      <Layout>
        <Card title="Error">
          <div className="error-container">
            <p>No se pudo cargar la información de la factura. Por favor, intente nuevamente.</p>
            <Button onClick={() => navigate('/')}>Volver al inicio</Button>
          </div>
        </Card>
      </Layout>
    );
  }
  
  // Manejador para el cambio en el monto de pago
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const amount = parseFloat(e.target.value);
    
    if (isNaN(amount) || amount <= 0) {
      setPaymentAmount(0);
      setIsPartialPayment(false);
      setShowPartialPaymentMessage(false);
      return;
    }
    
    setPaymentAmount(amount);
    
    // Determinar si es un pago parcial
    const isPartial = amount !== invoice.totalAmount;
    setIsPartialPayment(isPartial);
    setShowPartialPaymentMessage(isPartial);
  };
  
  // Manejador para continuar al pago
  const irAConfirmacion = () => {
    navigate('/payment-confirmation', {
      state: { invoice, paymentAmount, isPartialPayment }
    });
  };

  const handleContinue = async () => {
    if (await validarDiaHabil()) {
      irAConfirmacion();
    }
  };
  
  // Formatear números como moneda
  const formatCurrency = (value: number): string => {
    if (value === undefined || value === null || isNaN(value)) {
      return '$0';
    }
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(value);
  };

  return (
    <Layout>
      <Card title="Detalles de la Factura">
        <div className="invoice-detail">
          <div className="invoice-info">
            <h3>Detalles</h3>
            
            <div className="info-row">
              <span className="label">Número de Factura:</span>
              <span className="value">{invoice.invoiceNumber || 'N/A'}</span>
            </div>
            
            <div className="info-row">
              <span className="label">Cliente:</span>
              <span className="value">{invoice.clientName || 'N/A'}</span>
            </div>
            
            <div className="info-row">
              <span className="label">NIT/CC:</span>
              <span className="value">{invoice.clientId || 'N/A'}</span>
            </div>
            
            <div className="info-row">
              <span className="label">Fecha de Emisión:</span>
              <span className="value">{formatDate(invoice.issueDate)}</span>
            </div>
            
            <div className="info-row">
              <span className="label">Fecha de Vencimiento:</span>
              <span className="value">{formatDate(invoice.dueDate)}</span>
            </div>
            
            <div className="info-row">
              <span className="label">Detalle:</span>
              <span className="value detalle-line">{(invoice.description || 'Sin descripción').split('|').map((line, index) => (
            <React.Fragment key={index}>
              {line}
              {index < (invoice.description || '').split('|').length - 1 && <br />}
            </React.Fragment>
          ))}</span>            
            </div>
            
            <div className="info-row">
              <span className="label">Valor saldo de la Factura:</span>
              <span className="value highlight">{formatCurrency(invoice.total)}</span>
            </div>

            <div className="info-row">
              <span className="label">Intereses:</span>
              <span className="value highlight">{formatCurrency(Math.abs(invoice.paymentInterest || 0))}</span>
            </div>

            <div className="info-row">
              <span className="label">Monto total a pagar:</span>
              <span className="value highlight">{formatCurrency(invoice.totalAmount)}</span>
            </div>
          </div>
          
          <div className="payment-section">
            <h3>Información de Pago</h3>
            
            <InputField
              id="paymentAmount"
              label="Valor a Pagar"
              type="number"
              value={String(paymentAmount || 0)}
              onChange={handleAmountChange}
              min={1}
              max={invoice.totalAmount}
            />
            
            {showPartialPaymentMessage && (
              <div className="partial-payment-message">
                <p>El pago parcial será aplicado primero a intereses y el restante a capital</p>
              </div>
            )}

            <Button
              onClick={handleContinue}
              disabled={!paymentAmount || paymentAmount <= 0 || validando}
              fullWidth
            >
              {validando ? 'Validando...' : 'Continuar Pago'}
            </Button>
          </div>
        </div>
      </Card>

      <ModalDiaNoHabil
        visible={mostrarModal}
        mensaje={mensaje}
        onAceptar={() => {
          aceptarAviso();
          irAConfirmacion();
        }}
      />
    </Layout>
  );
};

export default InvoiceDetailPage; 